import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { maintenanceAPI, sensorAPI } from '../utils/api';
import { MaintenanceTemplate, MaintenanceSchedule, SensorData } from '../types';
import { Save, X, ArrowLeft, Calendar, Clock } from 'lucide-react';

const MaintenanceScheduleForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const generatorId = searchParams.get('generator_id');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<MaintenanceTemplate[]>([]);
  const [generators, setGenerators] = useState<SensorData[]>([]);
  const [schedule, setSchedule] = useState<Partial<MaintenanceSchedule>>({
    maintenance_template_id: undefined,
    generator_sensor_config_id: generatorId ? parseInt(generatorId) : undefined,
    last_service_date: undefined,
    last_service_run_hours: undefined,
    schedule_start_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  useEffect(() => {
    loadData();
    if (isEdit && id) {
      loadSchedule(parseInt(id));
    }
  }, [id, isEdit]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [templatesRes, sensorsRes] = await Promise.all([
        maintenanceAPI.getTemplates({ is_active: true }),
        sensorAPI.getAllSensors(),
      ]);
      setTemplates(templatesRes.templates);
      const generatorSensors = sensorsRes.sensors.filter(s => s.type === 'engine_hours');
      setGenerators(generatorSensors);
    } catch (error: any) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSchedule = async (scheduleId: number) => {
    try {
      setLoading(true);
      const response = await maintenanceAPI.getSchedule(scheduleId);
      setSchedule({
        maintenance_template_id: response.schedule.maintenance_template_id,
        generator_sensor_config_id: response.schedule.generator_sensor_config_id,
        last_service_date: response.schedule.last_service_date || undefined,
        last_service_run_hours: response.schedule.last_service_run_hours || undefined,
        schedule_start_date: response.schedule.schedule_start_date || new Date().toISOString().split('T')[0],
        notes: response.schedule.notes || '',
      });
    } catch (error: any) {
      console.error('Error loading schedule:', error);
      alert('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!schedule.maintenance_template_id || !schedule.generator_sensor_config_id) {
      alert('Please select both template and generator');
      return;
    }

    try {
      setSaving(true);
      
      if (isEdit && id) {
        await maintenanceAPI.updateSchedule(parseInt(id), schedule);
      } else {
        await maintenanceAPI.createSchedule(schedule);
      }
      
      navigate('/maintenance');
    } catch (error: any) {
      console.error('Error saving schedule:', error);
      alert(error.response?.data?.error || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/maintenance')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {isEdit ? 'Edit Schedule' : 'Create Schedule'}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {isEdit ? 'Update maintenance schedule' : 'Assign a template to a generator'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/maintenance')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Maintenance Template *
            </label>
            <select
              value={schedule.maintenance_template_id || ''}
              onChange={(e) => setSchedule({ ...schedule, maintenance_template_id: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a template...</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} ({template.scheduling_mode})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Generator *
            </label>
            <select
              value={schedule.generator_sensor_config_id || ''}
              onChange={(e) => setSchedule({ ...schedule, generator_sensor_config_id: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a generator...</option>
              {generators.map((gen) => (
                <option key={gen.id} value={gen.id}>
                  {gen.name}
                  {gen.device_info?.name && ` (${gen.device_info.name})`}
                  {gen.location ? ` - ${gen.location}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-md font-semibold text-gray-900 mb-4">Last Service Information (Optional)</h3>
            <p className="text-sm text-gray-600 mb-4">
              If this generator was recently serviced, enter the details below. Otherwise, leave blank to start tracking from today.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Last Service Date
                </label>
                <input
                  type="date"
                  value={schedule.last_service_date || ''}
                  onChange={(e) => setSchedule({ ...schedule, last_service_date: e.target.value || undefined })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Last Service Run Hours
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={schedule.last_service_run_hours || ''}
                  onChange={(e) => setSchedule({ ...schedule, last_service_run_hours: e.target.value ? parseFloat(e.target.value) : undefined })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Schedule Start Date
            </label>
            <input
              type="date"
              value={schedule.schedule_start_date || ''}
              onChange={(e) => setSchedule({ ...schedule, schedule_start_date: e.target.value || undefined })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={schedule.notes || ''}
              onChange={(e) => setSchedule({ ...schedule, notes: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Additional notes about this schedule..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceScheduleForm;

